import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DiscordChannelPicker } from '@/components/discord/DiscordPickers';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api';
import {
  useCreateTicketPanel,
  useDeleteTicketPanel,
  usePublishTicketPanel,
  useTicketCategories,
  useTicketPanels,
} from '@/lib/queries';
import { formatDate } from '@/lib/utils';

export function PanelsPage() {
  const panels = useTicketPanels();
  const cats = useTicketCategories();
  const create = useCreateTicketPanel();
  const remove = useDeleteTicketPanel();
  const publish = usePublishTicketPanel();

  const [open, setOpen] = useState(false);
  const [channelId, setChannelId] = useState('');
  const [selectedCats, setSelectedCats] = useState<Set<string>>(new Set());
  const [createLookupUrl, setCreateLookupUrl] = useState('');
  const [panelSecret, setPanelSecret] = useState<{ url: string; secret: string } | null>(null);

  function toggleCat(id: string) {
    setSelectedCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (selectedCats.size === 0) {
      toast.error('Select at least one category');
      return;
    }
    try {
      const created = await create.mutateAsync({
        channelId,
        embed: {
          title: 'Support',
          description: 'Click a button below to open a ticket.',
          color: 0x5865f2,
        },
        categoryIds: Array.from(selectedCats),
        customerLookupUrl: createLookupUrl || null,
      });
      toast.success('Panel created. Open it to finalize the embed.');
      setOpen(false);
      setChannelId('');
      setSelectedCats(new Set());
      setCreateLookupUrl('');
      if (created.customerLookupSecret) {
        setPanelSecret({
          url: created.customerLookupUrl ?? '',
          secret: created.customerLookupSecret,
        });
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handlePublish(id: string) {
    try {
      await publish.mutateAsync(id);
      toast.success('Publish command sent to the bot');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this panel?')) return;
    try {
      await remove.mutateAsync(id);
      toast.success('Deleted');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ticket panels</h1>
          <p className="text-muted-foreground">
            Discord embed messages with category buttons users click to open a ticket
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>New ticket panel</DialogTitle>
                <DialogDescription>
                  Fine-tune the embed on the panel page after creation.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Target channel</Label>
                  <DiscordChannelPicker value={channelId} onValueChange={setChannelId} />
                </div>
                <div className="space-y-2">
                  <Label>Categories (buttons)</Label>
                  <div className="space-y-1 rounded-md border border-input p-2">
                    {cats.data?.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Create categories first.</p>
                    ) : (
                      cats.data?.map((c) => (
                        <label
                          key={c.id}
                          className="flex cursor-pointer items-center gap-2 text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={selectedCats.has(c.id)}
                            onChange={() => toggleCat(c.id)}
                          />
                          {c.emoji && <span>{c.emoji}</span>}
                          {c.name}
                        </label>
                      ))
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="createLookupUrl">Customer lookup URL (optional)</Label>
                  <Input
                    id="createLookupUrl"
                    type="url"
                    value={createLookupUrl}
                    onChange={(e) => setCreateLookupUrl(e.target.value)}
                    placeholder="https://example.com/raynex/customer"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={create.isPending}>
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Panels</CardTitle>
          <CardDescription>{panels.data?.length ?? 0} total</CardDescription>
        </CardHeader>
        <CardContent>
          {panels.data && panels.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead>Categories</TableHead>
                  <TableHead>Published</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="w-40">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {panels.data.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <Link
                        to={`/tickets/panels/${p.id}`}
                        className="font-mono text-sm hover:underline"
                      >
                        {p.channelId}
                      </Link>
                    </TableCell>
                    <TableCell>{p.categoryIds.length}</TableCell>
                    <TableCell className="text-xs">
                      {p.messageId ? <span className="text-green-600">yes</span> : '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(p.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="secondary" size="sm" onClick={() => handlePublish(p.id)}>
                          <Send className="mr-1 h-3 w-3" />
                          Publish
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(panelSecret)} onOpenChange={(o) => !o && setPanelSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Customer lookup secret</DialogTitle>
            <DialogDescription>Copy now — won't be shown again.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-1">
              <Label>URL</Label>
              <Input
                readOnly
                value={panelSecret?.url ?? ''}
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
            <div className="space-y-1">
              <Label>Secret</Label>
              <Input
                readOnly
                value={panelSecret?.secret ?? ''}
                onFocus={(e) => e.currentTarget.select()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setPanelSecret(null)}>I saved it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
